import { DatePipe } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { DeploySendAttempt } from '@core/models/deploy-plan-config.model';

import { ModalComponent, ModalSize } from '@shared/components/modal/modal.component';
import { StatusPillComponent } from '@shared/components/status-pill/status-pill.component';

interface DeployHistoryEntry {
  attempt: DeploySendAttempt;
  /** 1-based position in send order, which is not the order the entries are listed in. */
  ordinal: number;
  total: number;
}

/**
 * Every recorded attempt on one message: when it ran, whether it landed, who it went to, what it
 * created and why it failed.
 *
 * Deliberately does not show what each attempt sent. `view` is rebuilt from current plan data on
 * every read and is not stored per attempt, so any content shown beside an old attempt would be
 * today's message rather than the one that went out.
 */
@Component({
  selector: 'app-deploy-send-history-modal',
  imports: [TranslatePipe, DatePipe, ModalComponent, StatusPillComponent],
  template: `
    <app-modal
      [title]="'features.plan.deploy.history.title' | translate: { label: label() }"
      [size]="ModalSize.XL"
      [config]="{ dismissible: true, closeOnBackdropClick: true }"
      (closeModal)="closed.emit()">
      <div modal-body class="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
        @for (entry of entries(); track entry.ordinal) {
          <div
            data-testid="deploy-history-entry"
            class="border-weak bg-surface flex flex-col gap-1.5 rounded-sm border p-2.5">
            <div class="flex flex-wrap items-center gap-2">
              <span
                data-testid="deploy-history-attempt"
                class="text-text-tertiary text-[10px] font-medium tracking-wide uppercase">
                {{
                  'features.plan.deploy.history.attempt'
                    | translate: { index: entry.ordinal, total: entry.total }
                }}
              </span>

              <app-status-pill
                data-testid="deploy-history-status"
                [variant]="entry.attempt.success ? 'success' : 'danger'"
                [dot]="true">
                {{
                  (entry.attempt.success
                    ? 'features.plan.deploy.table.sent_badge'
                    : 'features.plan.deploy.table.failed_badge'
                  ) | translate
                }}
              </app-status-pill>

              <!-- Seconds matter: GedMouv posts one tour per request and two attempts on the
                   same message can land a second apart, which a minute-precision stamp cannot
                   tell apart. -->
              <span
                data-testid="deploy-history-date"
                class="text-text-secondary ml-auto font-mono text-[11px] tabular-nums">
                {{ entry.attempt.sentAt | date: 'medium' }}
              </span>
            </div>

            @if (entry.attempt.recipients.length > 0) {
              <!-- The attempt's own addressees, which the table cannot show: its Recipients
                   column reports what the message resolves to *now*, so an address corrected
                   between two attempts is invisible everywhere else. -->
              <p
                data-testid="deploy-history-recipients"
                class="text-text-secondary text-xs">
                <span class="text-text-tertiary">
                  {{ 'features.plan.deploy.history.recipients' | translate }}
                </span>
                {{ entry.attempt.recipients.join(', ') }}
              </p>
            }

            @if (entry.attempt.externalId; as externalId) {
              <p
                data-testid="deploy-history-external-id"
                class="text-text-secondary font-mono text-[11px]">
                {{
                  'features.plan.deploy.table.external_id' | translate: { id: externalId }
                }}
              </p>
            }

            @if (entry.attempt.error; as error) {
              <div class="flex flex-col gap-0.5">
                <span class="text-text-secondary text-[10.5px] font-medium">
                  {{ 'features.plan.deploy.error_detail_label' | translate }}
                </span>
                <!-- The same soft-red panel the row and the deploy block use, so a failure
                     reads identically wherever it is reported, and pre-wrapped because a real
                     one runs to several lines of transport output. -->
                <div
                  class="bg-danger-soft border-danger overflow-hidden rounded-sm border px-2 py-1.5">
                  <p
                    data-testid="deploy-history-error"
                    class="text-text-primary font-mono text-[10.5px] leading-relaxed break-words whitespace-pre-wrap">
                    {{ error }}
                  </p>
                </div>
              </div>
            }
          </div>
        }
      </div>
    </app-modal>
  `,
  styles: ``
})
export class DeploySendHistoryModalComponent {
  readonly label = input.required<string>();
  readonly sends = input.required<readonly DeploySendAttempt[]>();

  readonly closed = output<void>();

  protected readonly ModalSize = ModalSize;

  /**
   * Newest first, numbered in send order. The wire order is oldest-first (`id asc`), which is
   * the wrong way round for reading what happened last — but the numbering has to stay in send
   * order, or "attempt 2" would name a different attempt here than in the tracking tables.
   */
  protected readonly entries = computed<DeployHistoryEntry[]>(() => {
    const sends = this.sends();
    return sends
      .map((attempt, index) => ({ attempt, ordinal: index + 1, total: sends.length }))
      .reverse();
  });
}
